import { Flex } from "@chakra-ui/react";
import { useEffect, useMemo, useState } from "react";
import { useAccountData } from "../../hooks/useAccountData";
import { GCChannel } from "../../types";
import { useLocation, useNavigate, useParams } from "react-router";
import { HOME_CHANNEL_ID, ORIGINALS_CHANNEL_ID } from "./constants";
import { HeaderNavigationBar } from "./components/HeaderNavigationBar";
import { useScrollVisibility } from "../../hooks/useScrollVisibility";
import { useTitleOnNavigation } from "../../hooks/useTitleOnNavigation";
import { t } from "i18next";
import { EmailVerificationBanner } from "./components/EmailVerificationBanner";
import { useUserData } from "../../hooks/useUserData";
import { CMSFeed } from "./components/CMSFeed";
import { useIsSmallBreakpoint } from "../../utils/useBreakpoints";

function RootScreen() {
  const location = useLocation();
  const { channel, slug } = useParams();
  const navigate = useNavigate();
  const accountData = useAccountData();
  const { userData } = useUserData();


  const homeTabChannels = accountData?.home_tab_channels ?? [];
  const isSmallBreakpoint = useIsSmallBreakpoint();

  const homeChannel = useMemo(() => {
    return (
      homeTabChannels.find((c) => c.id?.endsWith(HOME_CHANNEL_ID)) ??
      homeTabChannels.find((c) => c.is_primary) ??
      homeTabChannels[0]
    );
  }, [accountData]);

  const [activeChannel, setActiveChannel] = useState<GCChannel>();
  const [activeStreamId, setActiveStreamId] = useState<string>();

  const activeStream = activeChannel?.streams.find((stream) => {
    return stream.id === activeStreamId;
  });

  const isOriginalsChannel = channel === "originals";
  const isGcChannel = activeChannel?.id.endsWith(HOME_CHANNEL_ID);
  const originalChannel = accountData?.originals_channel;

  const isDealsRoute = location.pathname === "/deals";
  const isNewRoute = location.pathname === "/new";
  const isDirectStreamRoute = isDealsRoute || isNewRoute;

  var pageTitle = undefined;
  if (isDealsRoute && accountData?.deals_stream) {
    pageTitle = `${accountData.deals_stream.display_name} ${t("screens.root.channelsAtGlobalComix")}`;
  } else if (isNewRoute && accountData?.new_stream) {
    pageTitle = `${accountData.new_stream.display_name} ${t("screens.root.channelsAtGlobalComix")}`;
  } else if (!isGcChannel && !isOriginalsChannel) {
    if (activeStream?.display_name) {
      pageTitle = `${activeStream?.display_name} ${t("screens.root.channelsAtGlobalComix")}`;
    }
  } else {
    pageTitle = t("screens.root.readAndPublishComicsOnline");
  }

  useTitleOnNavigation(pageTitle);

  // Maps slugs to their channels
  const slugsMap = useMemo(() => {
    if (accountData) {
      const map: Record<string, GCChannel> = {};
      homeTabChannels.forEach((channel) => {
        channel.streams.forEach((s) => {
          map[s.slug] = channel;
        });
      });
      return map;
    }
    return undefined;
  }, [accountData]);

  // Handle /deals and /new routes — set the stream key directly from account data
  useEffect(() => {
    if (!isDirectStreamRoute || !accountData) return;

    const streamInfo = isDealsRoute
      ? accountData.deals_stream
      : accountData.new_stream;

    if (streamInfo) {
      setActiveChannel(undefined);
      setActiveStreamId(streamInfo.stream_key);
    }
  }, [location, accountData, isDirectStreamRoute]);

  /// User navigates to a channel, we need to set the active channel to the one they navigated to
  useEffect(() => {
    if (!slugsMap || isDirectStreamRoute) return;

    let channelExists = false;
    if (!channel && !slug) {
      if (homeChannel) findActiveStream(homeChannel);
      return;
    }

    if (isOriginalsChannel && originalChannel) {
      findActiveStream(originalChannel);
    } else {
      if (channel) {
        channelExists = !!slugsMap[channel];
      }
      const newActiveChannel = channel
        ? (slugsMap[`${channel}/${slug}`] ?? slugsMap[channel])
        : undefined;

      if (newActiveChannel) {
        findActiveStream(newActiveChannel);
      } else {
        if (homeChannel) findActiveStream(homeChannel);
        if (channelExists) {
          navigate(`/channel/${channel}`, { replace: true });
        } else {
          navigate("/", { replace: true });
        }
      }
    }
  }, [location, slugsMap, homeChannel]);

  /// Whenever the active channel changes, we want to set the active stream.
  /// In can be set the slug defined in the URL or the default first stream for the channel
  const findActiveStream = (channel: GCChannel) => {
    const newStream = channel.streams.find(function (stream) {
      return (
        (slug && stream.slug?.endsWith(slug)) ||
        (!slug && stream.id.endsWith("index")) ||
        (isOriginalsChannel && stream.id.endsWith("index"))
      );
    });
    setActiveChannel(channel);
    if (newStream) {
      setActiveStreamId(newStream.id);
    }
  };

  const { isHidden } = useScrollVisibility();

  return (
    <Flex flex={1} minWidth={0} direction="column">
      <CMSFeed
        header={
          activeChannel &&
          !isOriginalsChannel &&
          activeChannel?.id !== ORIGINALS_CHANNEL_ID ? (
            <HeaderNavigationBar
              activeChannel={activeChannel}
              activeStreamId={activeStreamId}
              showChannelName={!isGcChannel}
              isCollapsed={isHidden}
            />
          ) : null
        }
        activeStreamId={activeStreamId}
        fullSize={isHidden && isSmallBreakpoint}
        activeChannel={activeChannel}
        currentChannelSlug={channel}
        currentSlug={slug}
      />

      {userData && !userData?.verified && (
        <EmailVerificationBanner emailAddress={userData.email_address} />
      )}
    </Flex>
  );
}

export default RootScreen;
